import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  Box,
  Checkbox,
  LinearProgress,
  FormControl,
  Typography,
  Button,
  Skeleton,
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useDataProvider } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import { handleToggle } from '../utility/functions';
import { useDynamicFieldContext } from '../DynamicFieldContext';
import { SxProps, Theme } from '@mui/material/styles';
import { ApiErrorResponse, BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { GetDynamicTreeDataResponse } from 'providers/contexts/extensions/DynamicFieldData';
import { TFieldNode } from '../DynamicFormField';

export type TreeNode = TFieldNode & {
  children?: TreeNode[];
  isExpanded?: boolean;
};

type DenumTreeFieldProps = {
  value: { items: string[]; include_children?: boolean };
  onChange: (
    name: string,
    value: { items: string[]; include_children?: boolean },
  ) => void;
  sx?: SxProps<Theme>;
  disabled?: boolean;
};

export const DenumTreeField = memo(
  ({ value, onChange, sx, disabled = false }: DenumTreeFieldProps) => {
    const {
      selectedValues,
      updateSelectedValues,
      fieldConfig,
      searchText,
      includeChildren,
      setIncludeChildren,
    } = useDynamicFieldContext();
    const dataProvider = useDataProvider<BloxCustomDataProviderType>();
    const [treeData, setTreeData] = useState<TreeNode[]>([]);
    const [loading, setLoading] = useState<string | null>(null);
    const nodeOffsetSize = 4;

    // Normalize selectedValues to ensure an array
    const normalizedSelectedValues = useMemo(() => {
      if (Array.isArray(selectedValues)) {
        return selectedValues;
      }
      if (typeof selectedValues === 'object' && selectedValues?.items) {
        return selectedValues?.items;
      }
      return [];
    }, [selectedValues]);

    // Sync `value` with `selectedValues` from items array or direct array
    useEffect(() => {
      const values = Array.isArray(value.items)
        ? value.items
        : Array.isArray(value)
          ? value
          : [];
      updateSelectedValues(values);
      setIncludeChildren(value.include_children || false);
    }, [value, updateSelectedValues, setIncludeChildren]);

    const { data: initialNodes, isLoading: isInitialLoading } = useQuery<
      GetDynamicTreeDataResponse,
      ApiErrorResponse,
      GetDynamicTreeDataResponse['data']
    >({
      queryKey: [
        'denumTree',
        fieldConfig.config.app,
        fieldConfig.config.resolver,
        searchText,
      ],
      queryFn: async () => {
        const { app, resolver, params } = fieldConfig.config;
        const response = await dataProvider.getDynamicTreeData({
          app: app,
          resolver: resolver,
          parent: ',',
          params: {
            ...params,
            ...(searchText ? { query: searchText } : {}),
          },
        });
        return response;
      },
      select: (data) => data?.data,
      refetchOnWindowFocus: false,
      enabled: !!fieldConfig.config.resolver,
    });

    useEffect(() => {
      if (initialNodes?.items) {
        setTreeData(initialNodes.items);
      }
    }, [initialNodes]);

    const handleCheckboxChange = useCallback(
      (nodeValue: string) => {
        const updatedValues = handleToggle(normalizedSelectedValues, nodeValue, false);

        updateSelectedValues(updatedValues);

        onChange(fieldConfig.name, {
          items: updatedValues,
          ...(includeChildren && { include_children: true }),
        });
      },
      [
        normalizedSelectedValues,
        updateSelectedValues,
        onChange,
        fieldConfig.name,
        includeChildren,
      ],
    );

    const updateTreeData = useCallback(
      (nodes: TreeNode[], nodeId: number, updatedNode: Partial<TreeNode>): TreeNode[] => {
        return nodes.map((node) => {
          if (node.id === nodeId) {
            return {
              ...node,
              ...updatedNode,
              children: updatedNode.children || node.children,
            };
          }
          if (node.children) {
            return {
              ...node,
              children: updateTreeData(node.children, nodeId, updatedNode),
            };
          }
          return node;
        });
      },
      [],
    );

    const fetchChildNodes = useCallback(
      async (parentNode: TreeNode) => {
        const { app, resolver, params } = fieldConfig.config;

        setLoading(parentNode.id.toString());
        try {
          const response = await dataProvider.getDynamicTreeData({
            app: app,
            resolver: resolver,
            parent: parentNode.id.toString(),
            params: {
              ...params,
              ...(searchText ? { query: searchText } : {}),
            },
          });

          const respArray = (response?.data?.items ?? []) as TreeNode[];
          const childNodes = Array.isArray(respArray) ? respArray : [];

          setTreeData((prevTreeData) =>
            updateTreeData(prevTreeData, parentNode.id, {
              children: [...(parentNode.children || []), ...childNodes],
              isExpanded: true,
            }),
          );
        } catch (error) {
          console.error('Error fetching child nodes:', error);
        } finally {
          setLoading(null);
        }
      },
      [dataProvider, fieldConfig, searchText, updateTreeData],
    );

    const toggleNodeExpansion = useCallback(
      (node: TreeNode) => {
        if (!node.children && !node.isExpanded) {
          fetchChildNodes(node);
        } else {
          setTreeData((prevTreeData) =>
            updateTreeData(prevTreeData, node.id, {
              isExpanded: !node.isExpanded,
            }),
          );
        }
      },
      [fetchChildNodes, updateTreeData],
    );

    const renderedTree = useMemo(() => {
      const renderTree = (nodes: TreeNode[]) => {
        return nodes.map((node) => (
          <Box
            key={node.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              pl: nodeOffsetSize,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {!node.leaf && (
                  <Button
                    variant="text"
                    size="small"
                    disabled={loading === node.id.toString()}
                    onClick={() => toggleNodeExpansion(node)}
                    sx={{ minWidth: 'auto', ml: -nodeOffsetSize }}
                  >
                    {node.isExpanded ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowRightIcon fontSize="small" />
                    )}
                  </Button>
                )}
                <Checkbox
                  checked={normalizedSelectedValues.includes(node.value)}
                  onChange={() => handleCheckboxChange(node.value)}
                  size="small"
                  disabled={disabled}
                />
                <Typography>{node.label}</Typography>
              </Box>
            </Box>
            {loading === node.id.toString() ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: 1,
                  marginLeft: nodeOffsetSize,
                }}
              >
                <Box sx={{ marginRight: 1 }}>
                  <Skeleton
                    variant="rectangular"
                    width={20}
                    height={20}
                  />
                </Box>
                <Skeleton
                  variant="text"
                  width={150}
                />
              </Box>
            ) : node.isExpanded && node.children ? (
              <Box>{renderTree(node.children)}</Box>
            ) : null}
          </Box>
        ));
      };

      return renderTree(treeData);
    }, [
      treeData,
      loading,
      normalizedSelectedValues,
      handleCheckboxChange,
      toggleNodeExpansion,
      nodeOffsetSize,
      disabled,
    ]);

    return (
      <FormControl
        fullWidth
        variant="outlined"
        data-testid={`denum-tree-field--${fieldConfig.name}`}
        sx={sx}
      >
        {isInitialLoading ? (
          <LinearProgress />
        ) : treeData.length > 0 ? (
          renderedTree
        ) : (
          <Typography
            variant="body2"
            color="textSecondary"
          >
            No {fieldConfig.label} found.
          </Typography>
        )}
      </FormControl>
    );
  },
);
DenumTreeField.displayName = 'DenumTreeField';
